import { useRef } from "react";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

export default function ProblemSection() {
  const { ref: sectionRef, visible } = useScrollReveal(0.08);
  const { ref: textRef, visible: textVisible } = useScrollReveal(0.15);

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="relative w-full overflow-hidden bg-[#050507]"
      style={{ minHeight: "90vh" }}
    >
      {/* Full bleed image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f39e09daa9db68a7d137aa8c778c555a.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
          style={{
            opacity: visible ? 0.55 : 0,
            transform: visible ? "scale(1)" : "scale(1.04)",
            transition: "opacity 1.4s ease, transform 2s ease",
          }}
        />
        {/* Left-side text fade */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050507] via-[#050507]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050507]/80 via-transparent to-[#050507]/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-36 flex items-center min-h-[90vh]">
        <div
          ref={textRef as React.RefObject<HTMLDivElement>}
          className="max-w-xl"
          style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateX(0)" : "translateX(-40px)",
            transition: "opacity 1s ease 0.2s, transform 1s ease 0.2s",
          }}
        >
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-red-400/80 mb-5 block">
            The Problem
          </span>
          <h2
            className="font-extrabold text-white leading-[0.95] tracking-[-0.035em] mb-8"
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(2.8rem, 5vw, 5.5rem)",
            }}
          >
            Growth Without Structure{" "}
            <span className="text-red-400">Breaks Everything</span>
          </h2>
          <p
            className="text-white/55 leading-relaxed mb-10"
            style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.1rem" }}
          >
            Most businesses don&apos;t struggle with demand—they struggle with managing it. Missed
            leads, inconsistent follow-ups, and disconnected systems create instability that compounds
            over time.
          </p>

          <div className="space-y-4">
            {[
              "Missed leads and lost revenue opportunities",
              "Inconsistent follow-up kills conversion",
              "Disconnected tools create operational chaos",
            ].map((pain, i) => (
              <div
                key={pain}
                className="flex items-start gap-4"
                style={{
                  opacity: textVisible ? 1 : 0,
                  transform: textVisible ? "translateX(0)" : "translateX(-20px)",
                  transition: `opacity 0.8s ease ${0.4 + i * 0.12}s, transform 0.8s ease ${0.4 + i * 0.12}s`,
                }}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-close-circle-fill text-red-400/80 text-base" />
                </div>
                <span className="text-sm text-white/50 leading-relaxed">{pain}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
